import { useEffect, useMemo, useState } from "react";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Clock, User } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useArticles } from "@/hooks/use-articles";

// Format date for display
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-CA", { 
    day: "numeric", 
    month: "short", 
    year: "numeric" 
  });
};

const ArticlesPage = () => {
  const { data: articles, isLoading, error } = useArticles();
  const [selectedCategory, setSelectedCategory] = useState<string>("Tous");
  
  // Fixed list of categories (business-oriented)
  const categories = ["Tous", "Marketing B2B", "Création de contenu", "Marketing vidéo", "Stratégie", "Accompagnement"];
  
  // Filter articles based on selected category
  const filteredArticles = useMemo(() => {
    if (!articles) return [];
    if (selectedCategory === "Tous") return articles;
    return articles.filter(article => article.category === selectedCategory);
  }, [selectedCategory, articles]);

  // JSON-LD for article list page
  const blogListingSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog Agence 8020",
    "description": "Conseils, stratégies et insights pour générer des leads prévisibles pour votre business",
    "url": "https://agence8020.com/articles",
    "publisher": {
      "@type": "Organization",
      "name": "Agence 8020",
      "logo": {
        "@type": "ImageObject",
        "url": "https://agence8020.com/logo.png"
      }
    },
    "blogPost": (articles || []).map(article => ({
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.excerpt,
      "datePublished": article.published_date,
      "author": {
        "@type": "Organization",
        "name": article.author
      },
      "url": `https://agence8020.com/articles/${article.slug}`
    }))
  }), [articles]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Articles & Blog | Agence 8020 — Marketing pour entrepreneurs au Québec"
        description="Stratégies, guides et conseils pour générer des leads prévisibles grâce au contenu et à la pub Meta. Pour entrepreneurs et professionnels ambitieux au Québec."
        canonical="/articles"
        schemaJson={blogListingSchema}
      />

      <main className="min-h-screen bg-background">
        <Navbar />
        
        {/* Hero Section */}
        <header className="pt-32 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 gradient-hero" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] gradient-glow animate-pulse-glow" />
          
          <div className="container mx-auto px-6 relative z-10 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold mb-6"
            >
              <span className="text-foreground">Nos </span>
              <span className="text-primary">Articles</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto"
            >
              Conseils, stratégies et insights pour générer des leads prévisibles pour votre business. 
              Restez à jour avec les dernières tendances du marketing de contenu.
            </motion.p>
          </div>
        </header>

        {/* Category Filter */}
        <section className="pb-8" aria-label="Filtrer par catégorie">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="flex flex-wrap justify-center gap-3"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-primary text-primary-foreground"
                      : "bg-card border border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  }`}
                >
                  {category}
                </button>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-12" aria-label="Liste des articles">
          <div className="container mx-auto px-6">
            {isLoading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="rounded-2xl bg-card border border-border overflow-hidden">
                    <Skeleton className="aspect-video w-full" />
                    <div className="p-6 space-y-3">
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-destructive">Erreur lors du chargement des articles.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredArticles.map((article, index) => (
                  <Link 
                    key={article.id}
                    to={`/articles/${article.slug}`}
                    className="block"
                  >
                    <motion.article
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                      className="group cursor-pointer h-full"
                      layout
                    >
                      <div className="rounded-2xl bg-card border border-border overflow-hidden hover:border-primary/50 transition-all duration-500 h-full flex flex-col">
                        {/* Article Image */}
                        <div className="relative aspect-video overflow-hidden">
                          <img 
                            src={article.image_url || "https://agence8020.com/og-image.png"} 
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />
                          <Badge 
                            className="absolute bottom-4 left-4 bg-primary/90 text-primary-foreground hover:bg-primary/90 cursor-default"
                          >
                            {article.category}
                          </Badge>
                        </div>
                        
                        <div className="p-6 flex-1 flex flex-col">
                          <h2 className="font-heading text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                            {article.title}
                          </h2>
                          <p className="text-muted-foreground text-sm mb-6 flex-1">
                            {article.excerpt}
                          </p>
                          
                          {/* Meta */}
                          <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-border">
                            <div className="flex items-center gap-4">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" aria-hidden="true" />
                                <span>{article.read_time}</span>
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" aria-hidden="true" />
                                <span>{article.author}</span>
                              </span>
                            </div>
                            <time dateTime={article.published_date || ""}>
                              {formatDate(article.published_date)}
                            </time>
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  </Link>
                ))}
              </div>
            )}
            
            {/* No results message */}
            {!isLoading && !error && filteredArticles.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Aucun article trouvé dans cette catégorie.</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default ArticlesPage;
